import { useEffect, useMemo, useState } from 'react';
import { useQuery } from '@apollo/client';

import { GET_SCOPED_PRODUCT_SALES } from '../apollo/queries/productSales';
import type { ProductSalesWeekItem } from '../helpers/groupProductSalesByLast4Weeks';
import {
    getLastCompletedFinancialWeekSpan,
    getLastCompletedFinancialWeekWindows,
    type FinancialWeekWindow,
} from '../helpers/financialWeek';
import type { ProductSalesScopeInput } from './useProductSalesScope';
import { useAppConfig } from './useAppConfig';
import { useSalesDataMaintenance } from './useSalesDataMaintenance';

const WARM_REFETCH_DELAY_MS = 4000;
const WARM_REFETCH_MAX_ATTEMPTS = 3;

type ScopedProductSalesQueryData = {
    getScopedProductSales: {
        sales: ProductSalesWeekItem[];
        isWarming: boolean;
        storeCount: number;
        currencies: string[];
    };
};

type ScopedProductSalesQueryVars = {
    productId?: string;
    startDate?: Date;
    endDate?: Date;
    scope?: ProductSalesScopeInput;
};

type Params = {
    productId?: string | null;
    requestCreatedAt?: string | Date | null;
    skip?: boolean;
    scopeInput?: ProductSalesScopeInput;
};

function formatWeekNumbers(first: number, last: number): string {
    if (first === last) {
        return `FW${first}`;
    }
    return `FW${first}–FW${last}`;
}

function weekRangeLabel(
    weeks: FinancialWeekWindow[] | undefined,
    suffix = '',
): string {
    if (!weeks?.length) {
        return '';
    }
    const labels: string[] = [];
    let runStart = weeks[0].weekNumber;
    let runEnd = runStart;
    for (let index = 1; index < weeks.length; index += 1) {
        const { weekNumber } = weeks[index];
        if (weekNumber > runEnd) {
            runEnd = weekNumber;
        } else {
            labels.push(formatWeekNumbers(runStart, runEnd));
            runStart = weekNumber;
            runEnd = weekNumber;
        }
    }
    labels.push(formatWeekNumbers(runStart, runEnd));
    return `${labels.join(', ')}${suffix}`;
}

export function useProductSalesWindows({
    productId,
    requestCreatedAt,
    skip = false,
    scopeInput,
}: Params) {
    const { financialYearStartMonth } = useAppConfig();
    const salesMaintenance = useSalesDataMaintenance();
    const shouldSkip = skip || !productId || !requestCreatedAt;

    const windows = useMemo(() => {
        if (!requestCreatedAt) {
            return null;
        }
        return getLastCompletedFinancialWeekWindows(
            requestCreatedAt,
            financialYearStartMonth,
        );
    }, [requestCreatedAt, financialYearStartMonth]);

    const span = useMemo(() => {
        if (!requestCreatedAt) {
            return null;
        }
        return getLastCompletedFinancialWeekSpan(
            requestCreatedAt,
            financialYearStartMonth,
        );
    }, [requestCreatedAt, financialYearStartMonth]);
    const thisYearWindow = span?.thisYear ?? null;
    const lastYearWindow = span?.lastYear ?? null;

    const scopedSkip = shouldSkip || !thisYearWindow;

    const {
        data: thisYearScopedData,
        loading: thisYearScopedLoading,
        error: thisYearScopedError,
        refetch: refetchThisYearScoped,
    } = useQuery<ScopedProductSalesQueryData, ScopedProductSalesQueryVars>(
        GET_SCOPED_PRODUCT_SALES,
        {
            skip: scopedSkip,
            fetchPolicy: 'network-only',
            variables: {
                productId: productId ?? undefined,
                startDate: thisYearWindow?.startDate,
                endDate: thisYearWindow?.endDate,
                scope: scopeInput,
            },
        },
    );

    const {
        data: lastYearScopedData,
        loading: lastYearScopedLoading,
        error: lastYearScopedError,
        refetch: refetchLastYearScoped,
    } = useQuery<ScopedProductSalesQueryData, ScopedProductSalesQueryVars>(
        GET_SCOPED_PRODUCT_SALES,
        {
            skip: shouldSkip || !lastYearWindow,
            fetchPolicy: 'network-only',
            variables: {
                productId: productId ?? undefined,
                startDate: lastYearWindow?.startDate,
                endDate: lastYearWindow?.endDate,
                scope: scopeInput,
            },
        },
    );

    const thisYearSales = useMemo(
        (): ProductSalesWeekItem[] =>
            thisYearScopedData?.getScopedProductSales.sales ?? [],
        [thisYearScopedData],
    );

    const lastYearSales = useMemo(
        (): ProductSalesWeekItem[] =>
            lastYearScopedData?.getScopedProductSales.sales ?? [],
        [lastYearScopedData],
    );

    const isWarming = Boolean(
        thisYearScopedData?.getScopedProductSales.isWarming ||
            lastYearScopedData?.getScopedProductSales.isWarming,
    );

    const storeCount =
        thisYearScopedData?.getScopedProductSales.storeCount ??
        lastYearScopedData?.getScopedProductSales.storeCount;

    const currencies = useMemo(
        () => [
            ...new Set([
                ...(thisYearScopedData?.getScopedProductSales.currencies ?? []),
                ...(lastYearScopedData?.getScopedProductSales.currencies ?? []),
            ]),
        ],
        [thisYearScopedData, lastYearScopedData],
    );

    const hasMixedCurrencies = currencies.length > 1;

    const loading = thisYearScopedLoading || lastYearScopedLoading;

    const error = thisYearScopedError || lastYearScopedError;

    const hasSalesSource = Boolean(thisYearScopedData && lastYearScopedData);

    const warmKey = [
        productId,
        thisYearWindow?.startDate.toISOString(),
        scopeInput?.advertisingZoneId ?? '',
        (scopeInput?.storeIds ?? []).join(','),
    ].join('|');
    const [warmAttempts, setWarmAttempts] = useState(0);

    useEffect(() => {
        setWarmAttempts(0);
    }, [warmKey]);

    useEffect(() => {
        if (salesMaintenance.isActive || !isWarming) {
            return undefined;
        }
        if (warmAttempts >= WARM_REFETCH_MAX_ATTEMPTS) {
            return undefined;
        }

        const timeoutId = window.setTimeout(() => {
            setWarmAttempts(current => current + 1);
            refetchThisYearScoped().catch(() => undefined);
            refetchLastYearScoped().catch(() => undefined);
        }, WARM_REFETCH_DELAY_MS);

        return () => {
            window.clearTimeout(timeoutId);
        };
    }, [
        salesMaintenance.isActive,
        isWarming,
        warmKey,
        warmAttempts,
        refetchThisYearScoped,
        refetchLastYearScoped,
    ]);

    const showWarmingHint = isWarming && !salesMaintenance.isActive;
    const showMaintenanceNotice = salesMaintenance.isActive && isWarming;

    return {
        thisYearSales,
        lastYearSales,
        loading,
        error,
        hasSalesSource,
        isWarming,
        showWarmingHint,
        showMaintenanceNotice,
        storeCount: storeCount ?? 0,
        currencies,
        hasMixedCurrencies,
        thisYearWeekLabel: weekRangeLabel(windows?.thisYearWeeks),
        lastYearWeekLabel: weekRangeLabel(windows?.lastYearWeeks, ' last year'),
        thisYearWeeks: windows?.thisYearWeeks ?? [],
        lastYearWeeks: windows?.lastYearWeeks ?? [],
    };
}
